<template>
  <div class="financial-analytics">
    <!-- Header with Dimension Selection -->
    <div class="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm border">
      <div>
        <h2 class="mb-1 text-primary font-weight-bold h5">Financial Analytics</h2>
        <div class="d-flex align-items-center">
            <b-nav pills class="revenue-sub-nav">
              <b-nav-item :active="true" class="mode-toggle mr-3">
                <b-button-group size="sm">
                  <b-button :variant="activeMode === 'revenue' ? 'primary' : 'outline-primary'" @click="activeMode = 'revenue'">REVENUE</b-button>
                  <b-button :variant="activeMode === 'expense' ? 'danger' : 'outline-danger'" @click="activeMode = 'expense'">EXPENSE</b-button>
                </b-button-group>
              </b-nav-item>
              <b-nav-item :active="activeDim === 'summary'" @click="activeDim = 'summary'">Summary</b-nav-item>
              <b-nav-item :active="activeDim === 'client'" @click="activeDim = 'client'">Clients</b-nav-item>
              <b-nav-item :active="activeDim === 'vehicle'" @click="activeDim = 'vehicle'">Vehicles</b-nav-item>
              <b-nav-item :active="activeDim === 'shippingLine'" @click="activeDim = 'shippingLine'">Shipping Lines</b-nav-item>
              <b-nav-item :active="activeDim === 'month'" @click="activeDim = 'month'">Monthly</b-nav-item>
              <b-nav-item :active="activeDim === 'year'" @click="activeDim = 'year'">Yearly</b-nav-item>
            </b-nav>
        </div>
      </div>
      
      <div v-if="activeDim !== 'summary'" class="d-flex align-items-center">
        <b-button-group size="sm" class="mr-3 shadow-sm">
          <b-button :variant="viewMode === 'table' ? 'primary' : 'outline-primary'" @click="viewMode = 'table'">
            <md-icon size="16" class="mr-1">table_chart</md-icon> TABLE
          </b-button>
          <b-button :variant="viewMode === 'graph' ? 'primary' : 'outline-primary'" @click="viewMode = 'graph'">
            <md-icon size="16" class="mr-1">bar_chart</md-icon> BAR
          </b-button>
          <b-button :variant="viewMode === 'timeline' ? 'primary' : 'outline-primary'" @click="viewMode = 'timeline'">
            <md-icon size="16" class="mr-1">show_chart</md-icon> TIMELINE
          </b-button>
        </b-button-group>
        
        <b-form-input
          v-if="viewMode === 'table'"
          v-model="filter"
          placeholder="Search..."
          size="sm"
          style="width: 180px;"
          class="shadow-sm"
        />
      </div>
    </div>

    <!-- Summary View -->
    <div v-if="activeDim === 'summary'">
      <b-row>
        <b-col lg="4">
          <b-card shadow-sm class="mb-4 border-0 h-100">
            <h6 class="font-weight-bold text-dark mb-3">{{ activeMode === 'revenue' ? 'Revenue' : 'Expense' }} by Client</h6>
            <apexchart type="donut" height="280" :options="currentDonutOptions.client.options" :series="currentDonutOptions.client.series"></apexchart>
          </b-card>
        </b-col>
        <b-col lg="4">
          <b-card shadow-sm class="mb-4 border-0 h-100">
            <h6 class="font-weight-bold text-dark mb-3">{{ activeMode === 'revenue' ? 'Revenue' : 'Expense' }} by Vehicle</h6>
            <apexchart type="donut" height="280" :options="currentDonutOptions.vehicle.options" :series="currentDonutOptions.vehicle.series"></apexchart>
          </b-card>
        </b-col>
        <b-col lg="4">
          <b-card shadow-sm class="mb-4 border-0 h-100">
            <h6 class="font-weight-bold text-dark mb-3">{{ activeMode === 'revenue' ? 'Revenue' : 'Expense' }} by Shipping Line</h6>
            <apexchart type="donut" height="280" :options="currentDonutOptions.shippingLine.options" :series="currentDonutOptions.shippingLine.series"></apexchart>
          </b-card>
        </b-col>
      </b-row>
      <!-- Bottom Trends Summary -->
      <b-row>
        <b-col lg="12">
           <b-card shadow-sm class="border-0 text-center py-4">
              <div class="d-flex justify-content-around">
                <div v-for="(stat, idx) in summaryStats" :key="idx">
                   <div class="text-muted small text-uppercase font-weight-bold mb-1">{{ stat.label }}</div>
                   <h3 class="font-weight-bold mb-0" :class="stat.color">{{ stat.value }}</h3>
                </div>
              </div>
           </b-card>
        </b-col>
      </b-row>
    </div>
    <b-card v-if="activeDim !== 'summary'" no-body class="border-0 shadow-sm overflow-hidden">
      <!-- Detailed Content Area -->
      <!-- Table View -->
      <div v-if="viewMode === 'table'" class="p-0">
        <b-table
          :items="currentRawData"
          :fields="tableFields"
          :filter="filter"
          hover
          striped
          responsive
          show-empty
          class="mb-0 financial-table"
        >
          <template slot="index" slot-scope="data">
            {{ data.index + 1 }}
          </template>
          <template slot="value" slot-scope="data">
            <span class="font-weight-bold" :class="activeMode === 'revenue' ? 'text-success' : 'text-danger'">{{ formatCurrency(data.value) }}</span>
          </template>
          <template slot="pendingFine" slot-scope="data">
            <span class="text-warning font-weight-bold">{{ formatCurrency(data.value) }}</span>
          </template>
          <template slot="disposedFine" slot-scope="data">
            <span class="text-muted">{{ formatCurrency(data.value) }}</span>
          </template>
          <template slot="empty">
            <div class="text-center p-4 text-muted">No data found for this period</div>
          </template>
        </b-table>
      </div>

      <!-- Graph / Timeline View -->
      <div v-if="viewMode === 'graph' || viewMode === 'timeline'" class="p-4 bg-white position-relative">
        <div v-if="!currentRawData || !currentRawData.length" class="text-center py-5">
           <md-icon size="48" class="text-light mb-3">analytics</md-icon>
           <p class="text-muted">No analytics data available for this dimension</p>
        </div>
        <div v-else>
          <div class="d-flex justify-content-between align-items-center mb-4">
             <h6 class="font-weight-bold text-muted uppercase">{{ activeMode === 'revenue' ? 'Revenue' : 'Expense' }} Contribution: {{ dimensionLabel }}</h6>
             <span v-if="chartData && chartData.series && chartData.series[0]" class="small text-muted">
               Showing Top {{ chartData.series[0].data ? chartData.series[0].data.length : 0 }}
             </span>
          </div>
          <apexchart 
            :type="viewMode === 'timeline' ? 'area' : 'bar'" 
            height="450" 
            :options="chartData.options" 
            :series="chartData.series"
          ></apexchart>
        </div>
      </div>
    </b-card>

    <!-- Contextual Stats (Bottom) -->
    <b-row v-if="activeDim !== 'summary'" class="mt-4">
       <b-col lg="4" v-for="(stat, idx) in dimensionStats" :key="idx">
          <b-card class="border-0 shadow-sm text-center position-relative overflow-hidden">
             <div v-if="loading" class="loading-overlay"><b-spinner small variant="primary"></b-spinner></div>
             <div :class="{'loading-blur': loading}">
               <div class="small text-muted mb-1 text-uppercase font-weight-bold">{{ stat.label }}</div>
               <h4 class="mb-0 font-weight-bold" :class="stat.colorClass">{{ stat.value }}</h4>
             </div>
          </b-card>
       </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  name: 'FinancialAnalyticsTab',
  props: {
    loading: { type: Boolean, default: false },
    // Donut Data
    clientSeries: { type: Array, default: () => [] },
    clientOptions: { type: Object, default: () => ({}) },
    vehicleSeries: { type: Array, default: () => [] },
    vehicleOptions: { type: Object, default: () => ({}) },
    shippingLineSeries: { type: Array, default: () => [] },
    shippingLineOptions: { type: Object, default: () => ({}) },
    
    // Raw Data for Tables
    clientRawData: { type: Array, default: () => [] },
    vehicleRawData: { type: Array, default: () => [] },
    slRawData: { type: Array, default: () => [] },
    monthlyRawData: { type: Array, default: () => [] },
    yearlyRawData: { type: Array, default: () => [] },

    // Expense Data
    clientExpenseSeries: { type: Array, default: () => [] },
    clientExpenseOptions: { type: Object, default: () => ({}) },
    vehicleExpenseSeries: { type: Array, default: () => [] },
    vehicleExpenseOptions: { type: Object, default: () => ({}) },
    shippingLineExpenseSeries: { type: Array, default: () => [] },
    shippingLineExpenseOptions: { type: Object, default: () => ({}) },
    monthlyRevenueSeries: { type: Array, default: () => [] },
    monthlyRevenueOptions: { type: Object, default: () => ({}) },
    yearlyRevenueSeries: { type: Array, default: () => [] },
    yearlyRevenueOptions: { type: Object, default: () => ({}) },
    monthlyExpenseSeries: { type: Array, default: () => [] },
    monthlyExpenseOptions: { type: Object, default: () => ({}) },
    yearlyExpenseSeries: { type: Array, default: () => [] },
    yearlyExpenseOptions: { type: Object, default: () => ({}) },
    clientExpenseRaw: { type: Array, default: () => [] },
    vehicleExpenseRaw: { type: Array, default: () => [] },
    slExpenseRaw: { type: Array, default: () => [] },
    monthlyExpenseRaw: { type: Array, default: () => [] },
    yearlyExpenseRaw: { type: Array, default: () => [] }
  },
  data() {
    return {
      activeDim: 'summary',
      activeMode: 'revenue',
      viewMode: 'table',
      filter: '',
    };
  },
  computed: {
    tableFields() {
      const modeLabel = this.activeMode === 'revenue' ? 'Revenue' : 'Expense';
      return [
        { key: 'index', label: 'SN.' },
        { key: 'label', label: 'Dimension', sortable: true },
        { key: 'value', label: modeLabel, sortable: true },
        { key: 'pendingFine', label: 'Pending Fine', sortable: true },
        { key: 'disposedFine', label: 'Disposed Fine', sortable: true }
      ];
    },
    currentRawData() {
      const suffix = this.activeMode === 'revenue' ? '' : 'Expense';
      const key = `${this.activeDim}${suffix}Raw`
        .replace('shippingLine', 'sl')
        .replace('month', 'monthly')
        .replace('year', 'yearly');
      
      // Fallback for special mapping of prop names
      if (this.activeMode === 'expense') {
        if (this.activeDim === 'client') return this.clientExpenseRaw;
        if (this.activeDim === 'vehicle') return this.vehicleExpenseRaw;
        if (this.activeDim === 'shippingLine') return this.slExpenseRaw;
        if (this.activeDim === 'month') return this.monthlyExpenseRaw;
        if (this.activeDim === 'year') return this.yearlyExpenseRaw;
      } else {
        if (this.activeDim === 'client') return this.clientRawData;
        if (this.activeDim === 'vehicle') return this.vehicleRawData;
        if (this.activeDim === 'shippingLine') return this.slRawData;
        if (this.activeDim === 'month') return this.monthlyRawData;
        if (this.activeDim === 'year') return this.yearlyRawData;
      }
      return [];
    },
    currentDonutOptions() {
      if (this.activeMode === 'revenue') {
        return {
          client: { series: this.clientSeries, options: this.clientOptions },
          vehicle: { series: this.vehicleSeries, options: this.vehicleOptions },
          shippingLine: { series: this.shippingLineSeries, options: this.shippingLineOptions }
        };
      } else {
        return {
          client: { series: this.clientExpenseSeries, options: this.clientExpenseOptions },
          vehicle: { series: this.vehicleExpenseSeries, options: this.vehicleExpenseOptions },
          shippingLine: { series: this.shippingLineExpenseSeries, options: this.shippingLineExpenseOptions }
        };
      }
    },
    dimensionLabel() {
      const map = {
        client: 'Clients',
        vehicle: 'Vehicles',
        shippingLine: 'Shipping Lines',
        month: 'Monthly Trend',
        year: 'Annual Comparison'
      };
      return map[this.activeDim];
    },
    summaryStats() {
      try {
        const data = this.clientRawData || [];
        const totalRev = data.reduce((s, i) => s + (i.value || 0), 0);
        const vehicleCount = (this.vehicleRawData || []).length;
        return [
          { label: 'Total Fleet Revenue', value: this.formatCurrencyShort(totalRev), color: 'text-primary' },
          { label: 'Active Clients', value: data.length, color: 'text-success' },
          { label: 'Revenue/Vehicle', value: this.formatCurrencyShort(vehicleCount ? totalRev / vehicleCount : 0), color: 'text-info' }
        ];
      } catch (e) {
        console.error('Error in summaryStats:', e);
        return [];
      }
    },
    chartData() {
      try {
        const data = (this.currentRawData || []).slice(0, 15);
        if (!data.length) return { series: [], options: {} };

        const isTimeline = this.viewMode === 'timeline';
        const label = this.activeMode === 'revenue' ? 'Revenue' : 'Expense';
        const series = [{ name: label, data: data.map(d => d.value || 0) }];
        
        const options = {
          chart: { 
            type: isTimeline ? 'area' : 'bar', 
            toolbar: { show: true },
            zoom: { enabled: isTimeline }
          },
          stroke: { curve: 'smooth', width: isTimeline ? 3 : 0 },
          fill: {
            type: isTimeline ? 'gradient' : 'solid',
            gradient: isTimeline ? { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } : undefined
          },
          plotOptions: { 
            bar: { borderRadius: 4, horizontal: !isTimeline, distributed: !isTimeline, barHeight: '70%' } 
          },
          colors: isTimeline ? ['#3b82f6'] : ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
          dataLabels: { enabled: !isTimeline, formatter: (val) => this.formatCurrencyShort(val), style: { fontSize: '10px' } },
          xaxis: { 
            categories: data.map(d => d.label || 'N/A'), 
            labels: { formatter: (val) => isTimeline ? val : this.formatCurrencyShort(val) } 
          },
          yaxis: {
            labels: { formatter: (val) => this.formatCurrencyShort(val) }
          },
          tooltip: { y: { formatter: (val) => this.formatCurrency(val) } },
          legend: { show: false }
        };
        return { series, options };
      } catch (e) {
        console.error('Error in chartData:', e);
        return { series: [], options: {} };
      }
    },
    dimensionStats() {
      try {
        const data = this.currentRawData || [];
        const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
        const count = data.length;
        const totalPending = data.reduce((sum, item) => sum + (item.pendingFine || 0), 0);
        const totalDisposed = data.reduce((sum, item) => sum + (item.disposedFine || 0), 0);
        
        return [
          { label: `Total ${this.dimensionLabel || 'Items'}`, value: count, colorClass: 'text-primary' },
          { label: `Total ${this.activeMode === 'revenue' ? 'Revenue' : 'Expense'}`, value: this.formatCurrencyShort(total), colorClass: 'text-success' },
          { label: 'Total Pending Fines', value: this.formatCurrencyShort(totalPending), colorClass: 'text-warning' }
        ];
      } catch (e) {
        console.error('Error in dimensionStats:', e);
        return [];
      }
    }
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return '₹0';
      return '₹' + Number(val).toLocaleString('en-IN');
    },
    formatCurrencyShort(val) {
      if (val === null || val === undefined || isNaN(val)) return '₹0';
      const num = Number(val);
      if (num >= 10000000) return '₹' + (num / 10000000).toFixed(2) + 'Cr';
      if (num >= 100000) return '₹' + (num / 100000).toFixed(2) + 'L';
      if (num >= 1000) return '₹' + (num / 1000).toFixed(1) + 'K';
      return '₹' + num.toFixed(0);
    }
  }
};
</script>

<style scoped>
.financial-analytics { padding: 10px; background: #f8fafc; }
.revenue-sub-nav .nav-link { color: #64748b; font-weight: 600; font-size: 0.9rem; padding: 8px 16px; border-radius: 8px; margin-right: 5px; transition: all 0.2s; }
.revenue-sub-nav .nav-link.active { background: #3b82f6 !important; color: white !important; box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3); }
.financial-table ::v-deep th { background: #f1f5f9; text-transform: uppercase; font-size: 0.75rem; color: #475569; border-top: none; }
.financial-table ::v-deep td { vertical-align: middle; padding: 12px 16px; }
.financial-table ::v-deep .dot.expense { background-color: #ef4444; }

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  backdrop-filter: blur(2px);
}
.loading-blur {
  filter: blur(1px);
  opacity: 0.6;
}
</style>



// WEBPACK FOOTER //
// FinancialAnalyticsTab.vue?7e1ebe24